import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { deleteMessageFromChat } from '../../actions/sendMessage';
import { retrySendMessageById } from '../../actions/sendMessage/retrySendMessageById';
import { DocumentMessage } from '../../components/DocumentMessage';
import { currentOpenChatIDSelector } from '../../selectors';
import { TMessage, TOutcomingStatus } from '../../types/messages';
import { useApplicationContext } from '../../utils/applicationContext';
import { formatBytes } from '../../utils/formatBytes';
import { getFileSrc } from '../../utils/getFileSrc';
import { useDocument } from '../ChatMessageContainer/hooks';

type TDocumentMessageContainerProps = {
  message: TMessage & { status: TOutcomingStatus };
  messageId: string;
};

export const DocumentMessageContainer: React.FC<TDocumentMessageContainerProps> = ({ message, messageId }) => {
  const { files, isOutcoming, status } = message;
  const { document, documentDraft } = useDocument(messageId, files?.documents?.[0]);
  const dispatch = useDispatch();

  const chatId = useSelector(currentOpenChatIDSelector);
  const { custom } = useApplicationContext();

  const handleCancel = React.useCallback(() => {
    if (chatId && documentDraft) {
      documentDraft.cancellationToken.cancel();
      dispatch(deleteMessageFromChat({ chatId, messageId, messagesMap: custom.messagesMap }));
    }
  }, [chatId, custom.messagesMap, dispatch, documentDraft, messageId]);

  const handleReload = React.useCallback(() => {
    dispatch(retrySendMessageById(chatId as string, messageId));
  }, [chatId, dispatch, messageId]);

  const src = React.useMemo(() => {
    if (document?.uid) {
      return getFileSrc(document.uid, chatId);
    }

    return null;
  }, [chatId, document?.uid]);

  const name = React.useMemo(() => {
    return document?.name || documentDraft?.file?.name || '';
  }, [document?.name, documentDraft?.file?.name]);

  const size = React.useMemo(() => {
    const sizeNumber = document?.size || documentDraft?.file?.size;

    return formatBytes(sizeNumber);
  }, [document?.size, documentDraft?.file?.size]);

  if (!documentDraft && !document) {
    return null;
  }

  return (
    <DocumentMessage
      isOutcoming={isOutcoming}
      messageStatus={status}
      name={name}
      size={size}
      src={src}
      onCancel={handleCancel}
      onReload={handleReload}
    />
  );
};
